export interface LoginRequest {
  Email: string;
  Password: string;
}

/**
 * Login/verify-otp/resend-otp/refresh-token all return their Data payload as a C#
 * `dynamic ExpandoObject`, not a typed POCO — System.Text.Json's camelCase naming
 * policy only rewrites reflected POCO property names, not ExpandoObject/dictionary
 * keys, so these come back exactly as PascalCase on the wire (unlike Success/Message
 * on the same response, which ARE real POCO properties and do get camelCased).
 */
export interface LoginResponseData {
  RequiresOtp: boolean;
  AccessToken?: string;
  UniqueUserId: string;
  /** Identifies this specific OTP issuance row (UserOTP.UniqueId) — required by verify-otp, present when RequiresOtp is true. */
  UniqueId?: string;
}

/**
 * Failure payload of login / verify-otp / resend-otp (PascalCase, same ExpandoObject casing as above).
 * IsLoginBlocked = OTP block, account lock, or reset-required — not just a wrong code/password.
 */
export interface LoginBlockedData {
  IsLoginBlocked?: boolean;
  /** Seconds left on a temporary OTP block; null for locked / reset-required. */
  BlockedSeconds?: number | null;
}

/** Seconds left on the block from an auth failure payload, if any. */
export function getBlockedSeconds(data: unknown): number | null {
  return (data as LoginBlockedData | null | undefined)?.BlockedSeconds ?? null;
}

/**
 * Login block/lock notice for one email (shown under Sign in).
 * `until` (epoch ms) is set for a temporary OTP block (countdown); null for locked / reset-required.
 */
export interface LoginBlock {
  email: string;
  message: string;
  until: number | null;
}

/** Router state key used to hand a LoginBlock from the OTP page to the Login page. */
export const LOGIN_BLOCK_STATE_KEY = 'loginBlock';

/** `blockedSeconds` comes from the server (seconds left), so the user's clock can't skew the countdown. */
export function createLoginBlock(email: string, message: string, blockedSeconds: number | null | undefined): LoginBlock {
  return {
    email: email.trim().toLowerCase(),
    message,
    until: blockedSeconds && blockedSeconds > 0 ? Date.now() + blockedSeconds * 1000 : null
  };
}

/** True when an auth failure payload says the user is blocked/locked (show a dialog, not a toast). */
export function isLoginBlocked(data: unknown): boolean {
  return (data as LoginBlockedData | null | undefined)?.IsLoginBlocked === true;
}

export interface VerifyOtpRequest {
  UniqueUserId: string;
  OTP: string;
  /** Must match the UniqueId returned by whichever login/resend-otp call issued this OTP. */
  UniqueId: string;
}

export interface VerifyOtpResponseData {
  AccessToken: string;
  UniqueUserId: string;
}

export interface ResendOtpRequest {
  UniqueUserId: string;
}

export interface ResendOtpResponseData {
  RequiresOtp: boolean;
  UniqueUserId: string;
  /** A fresh UserOTP row is issued on every resend — replace any previously held UniqueId with this one. */
  UniqueId: string;
}

/**
 * Clear, semantic view of the JWT's claims (decoded and mapped by AuthService.completeLogin
 * from the raw payload's JWT-spec abbreviations — see JwtClaims in jwt.service.ts — since `sub`/`jti` etc.
 * aren't meaningful names to read/write throughout the rest of the app).
 */
export interface TokenData {
  userId: string;
  roleId: string;
  roleName: string;
  loginTokenId: string;
  email: string;
  fullName: string;
  firstName: string;
  lastName: string;
  isPrimaryUser: string;
  isSuperAdmin: string;
  isFirstTimeLoggedIn: string;
  tokenExpiresAt: Date;
}

export interface ForgotPasswordRequest {
  EmailId: string;
}

export interface ValidateForgotPasswordLinkRequest {
  ResetPasswordToken: string;
}

export interface ResetPasswordRequest {
  ResetPasswordToken: string;
  NewPassword: string;
  ConfirmPassword: string;
}

export interface SetInitialPasswordRequest {
  NewPassword: string;
  ConfirmPassword: string;
}

export interface RequestChangePasswordRequest {
  OldPassword: string;
  CreatePassword: string;
  ConfirmPassword: string;
}

export interface RequestChangePasswordResponseData {
  UniqueId: string;
}

export interface VerifyChangePasswordOtpRequest {
  OTP: string;
  UserId: string;
  UniqueId: string;
  NewPassword: string;
}

export interface ApiMessageResponse {
  message?: string;
}

export const OTP_DURATION_SECONDS = 300;
export const OTP_DIGITS = 6;

export interface VerifyOtpState {
  uniqueUserId?: string;
  uniqueId?: string;
  email: string;
  otpExpiresAt?: number;
}

export interface UserProfile {
  firstName?: string;
  lastName?: string;
  emailId?: string;
  phoneNo?: string;
  profileFileName?: string | null;
}
